import axios, {AxiosResponse} from 'axios';
import Cookies from 'js-cookie';
import {ACCESS_TOKEN, MSG_SET_LOADING_STYLE, MSG_SHOW_SNACKBAR, SHOW_LOADING, UNDEF} from './Constants';
import {getCenterOf, localStorageGet, localStorageSave, postMessage} from './Util/Util';
import {ResponseCallback} from 'types';

export function getAccessToken() {
    return Cookies.get(ACCESS_TOKEN) || localStorageGet(ACCESS_TOKEN);
}

// ---------------------------------------------------------------------------
// URL resolution with fallback domain support
// ---------------------------------------------------------------------------

let resolvedV1Url: string = process.env.REACT_APP_API_URL || '';
let resolvedV3Url: string = process.env.REACT_APP_API_V3_URL || '';
let v1PingPromise: Promise<void> | null = null;
let v3PingPromise: Promise<void> | null = null;

const PING_TIMEOUT_MS = parseInt(process.env.REACT_APP_API_PING_TIMEOUT_MS || '', 10) || 2000;

async function pingUrl(baseUrl: string): Promise<boolean> {
    if (!baseUrl) return false;
    try {
        await axios.get(`${baseUrl}/_/ping`, {timeout: PING_TIMEOUT_MS});
        return true;
    } catch {
        return false;
    }
}

async function ensureV1UrlResolved(): Promise<void> {
    if (v1PingPromise !== null) return v1PingPromise;
    v1PingPromise = (async () => {
        const primary = process.env.REACT_APP_API_URL || '';
        const fallbacks = (process.env.REACT_APP_API_URL_FALLBACK || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

        if (await pingUrl(primary)) {
            resolvedV1Url = primary;
            return;
        }
        for (const url of fallbacks) {
            if (await pingUrl(url)) {
                resolvedV1Url = url;
                return;
            }
        }
        showSnackbar('Unable to connect to the server. Please check your network connection.', 'error');
        resolvedV1Url = primary;
    })();
    return v1PingPromise;
}

async function ensureV3UrlResolved(): Promise<void> {
    if (v3PingPromise !== null) return v3PingPromise;
    v3PingPromise = (async () => {
        const primary = process.env.REACT_APP_API_V3_URL || '';
        const fallbacks = (process.env.REACT_APP_API_V3_URL_FALLBACK || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

        if (await pingUrl(primary)) {
            resolvedV3Url = primary;
            return;
        }
        for (const url of fallbacks) {
            if (await pingUrl(url)) {
                resolvedV3Url = url;
                return;
            }
        }
        showSnackbar('Unable to connect to the server. Please check your network connection.', 'error');
        resolvedV3Url = primary;
    })();
    return v3PingPromise;
}

// ---------------------------------------------------------------------------
// Standalone request helpers (v1)
// ---------------------------------------------------------------------------

export async function GET(url: string, headers?: any) {
    const isAbsolute = url.startsWith('http') || url[0] === '/';
    if (!isAbsolute) await ensureV1UrlResolved();
    const path = isAbsolute ? url : resolvedV1Url + '/' + url;
    const noCacheHeaders = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
    };
    const reqHeaders = {
        ...noCacheHeaders,
        ...(headers || {}),
        Authorization: `Bearer ${getAccessToken()}`,
    };
    return axios.get(path, {withCredentials: true, headers: reqHeaders});
}

/**
 * `Authorization: Bearer` is always set last and will override any value in `headers`.
 * Pass `headers` for extra headers only — not to substitute a different token.
 */
export async function POST(url: string, params: any, headers?: any) {
    const isAbsolute = url.startsWith('http') || url[0] === '/';
    if (!isAbsolute) await ensureV1UrlResolved();
    const path = isAbsolute ? url : resolvedV1Url + '/' + url;
    const reqHeaders = {...(headers || {}), Authorization: `Bearer ${getAccessToken()}`};
    return axios.post(path, params, {withCredentials: true, headers: reqHeaders});
}

/**
 * `Authorization: Bearer` is always set last and will override any value in `headers`.
 * Pass `headers` for extra headers only — not to substitute a different token.
 * Pass `skipAuth: true` for pre-authenticated URLs (e.g. S3 pre-signed) that must not receive a Bearer token.
 */
export async function PUT(url: string, data: any, headers?: any, skipAuth = false) {
    const isAbsolute = url.startsWith('http') || url[0] === '/';
    if (!isAbsolute) await ensureV1UrlResolved();
    const path = isAbsolute ? url : resolvedV1Url + '/' + url;
    const reqHeaders = skipAuth
        ? {...(headers || {})}
        : {...(headers || {}), Authorization: `Bearer ${getAccessToken()}`};
    return axios.put(path, data, {withCredentials: true, headers: reqHeaders});
}

// ---------------------------------------------------------------------------
// Standalone request helpers (v3)
// ---------------------------------------------------------------------------

function buildV3Url(path: string): string {
    return resolvedV3Url + (path.startsWith('/') ? path : '/' + path);
}

/**
 * `Authorization: Bearer` is always set last and will override any value in `headers`.
 * Pass `headers` for extra headers only — not to substitute a different token.
 */
export async function GETv3(path: string, headers?: any) {
    await ensureV3UrlResolved();
    const noCacheHeaders = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
    };
    const reqHeaders = {
        ...noCacheHeaders,
        ...(headers || {}),
        Authorization: `Bearer ${getAccessToken()}`,
    };
    return axios.get(buildV3Url(path), {withCredentials: true, headers: reqHeaders});
}

/**
 * `Authorization: Bearer` is always set last and will override any value in `headers`.
 * Pass `headers` for extra headers only — not to substitute a different token.
 */
export async function POSTv3(path: string, params: any, headers?: any) {
    await ensureV3UrlResolved();
    const reqHeaders = {...(headers || {}), Authorization: `Bearer ${getAccessToken()}`};
    return axios.post(buildV3Url(path), params, {withCredentials: true, headers: reqHeaders});
}

/**
 * `Authorization: Bearer` is always set last and will override any value in `headers`.
 * Pass `headers` for extra headers only — not to substitute a different token.
 */
export async function PUTv3(path: string, data: any, headers?: any) {
    await ensureV3UrlResolved();
    const reqHeaders = {...(headers || {}), Authorization: `Bearer ${getAccessToken()}`};
    return axios.put(buildV3Url(path), data, {withCredentials: true, headers: reqHeaders});
}

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------

export function FB_GET(url: string) {
    axios
        .get(url)
        .then(function (response) {
            // handle success
            console.log(response);
            return response;
        })
        .catch(function (error) {
            // handle error
            console.log(error);
        })
        .then(function () {
            // always executed
        });
}

export function showLoading(show = true, style: any = null) {
    postMessage(SHOW_LOADING, show ? 1 : -1, 1);
    style !== null && postMessage(MSG_SET_LOADING_STYLE, style);
    return true;
}

export function showSnackbar(
    msg: string,
    variant: string,
    durationMilli?: number | undefined,
    actionTitle?: string,
    onAction?: any,
) {
    postMessage(MSG_SHOW_SNACKBAR, {
        key: new Date().getTime(),
        variant: variant,
        message: msg,
        duration: durationMilli,
        actionTitle: actionTitle,
        onAction: onAction,
    });
}

// ---------------------------------------------------------------------------
// Api class — wraps request helpers with loading indicator + error snackbar
// ---------------------------------------------------------------------------

export default class Api {
    private static call(
        method: string,
        url: string,
        params?: any,
        respCallback?: ResponseCallback,
        showLoader: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
        skipAuth = false,
    ) {
        if (showLoader) {
            if (showLoader instanceof HTMLElement) {
                const center = getCenterOf(showLoader);
                postMessage(MSG_SET_LOADING_STYLE, {
                    position: 'absolute',
                    left: center.x - 30,
                    top: center.y - 31,
                });
            } else {
                postMessage(MSG_SET_LOADING_STYLE, {});
            }
            showLoading();
        }

        const request =
            method === 'GET'
                ? GET(url, headers)
                : method === 'POST'
                  ? POST(url, params, headers)
                  : PUT(url, params, headers, skipAuth);
        request.then(
            res => {
                showLoader && showLoading(false);
                respCallback && respCallback(res);

                const data = res && res.data;
                const success = data && data.success;
                const message = data && data.message;
                if (success === false && showErrorSnackbar) {
                    const actionBtnTxt = showRetry ? 'Retry' : UNDEF;
                    const callback = !actionBtnTxt
                        ? UNDEF
                        : () => {
                              Api.call(method, url, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
                          };
                    showSnackbar(message, 'warning', UNDEF, actionBtnTxt, callback);
                }
            },
            err => {
                showLoader && showLoading(false);
                respCallback && respCallback(UNDEF, err);

                if (showErrorSnackbar) {
                    const resp = err.response;

                    // Don't show network errors (have another snackbar for that)
                    if (!resp) return;

                    const data = resp && resp.data;
                    const error = (data && (data.error_description || data.message || data.error)) || err.message;

                    const isAuthProblem = resp && resp.status === 401;
                    const relog =
                        isAuthProblem &&
                        (() => {
                            localStorageSave('user_info');
                            window.location.reload();
                        });
                    const actionBtnTxt = isAuthProblem ? 'Re-Login' : showRetry ? 'Retry' : UNDEF;
                    const callback = !actionBtnTxt
                        ? UNDEF
                        : isAuthProblem
                          ? relog
                          : () => {
                                Api.call(method, url, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
                            };
                    showSnackbar(error, 'error', UNDEF, actionBtnTxt, callback);
                }
            },
        );
    }

    private static callV3(
        method: string,
        path: string,
        params?: any,
        respCallback?: ResponseCallback,
        showLoader: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
    ) {
        if (showLoader) {
            if (showLoader instanceof HTMLElement) {
                const center = getCenterOf(showLoader);
                postMessage(MSG_SET_LOADING_STYLE, {
                    position: 'absolute',
                    left: center.x - 30,
                    top: center.y - 31,
                });
            } else {
                postMessage(MSG_SET_LOADING_STYLE, {});
            }
            showLoading();
        }

        const request =
            method === 'GET'
                ? GETv3(path, headers)
                : method === 'POST'
                  ? POSTv3(path, params, headers)
                  : PUTv3(path, params, headers);
        request.then(
            res => {
                showLoader && showLoading(false);
                respCallback && respCallback(res);

                const data = res && res.data;
                const success = data && data.success;
                const message = data && data.message;
                if (success === false && showErrorSnackbar) {
                    const actionBtnTxt = showRetry ? 'Retry' : UNDEF;
                    const callback = !actionBtnTxt
                        ? UNDEF
                        : () => {
                              Api.callV3(method, path, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
                          };
                    showSnackbar(message, 'warning', UNDEF, actionBtnTxt, callback);
                }
            },
            err => {
                showLoader && showLoading(false);
                respCallback && respCallback(UNDEF, err);

                if (showErrorSnackbar) {
                    const resp = err.response;

                    if (!resp) return;

                    const data = resp && resp.data;
                    const error = (data && (data.error_description || data.message || data.error)) || err.message;

                    const isAuthProblem = resp && resp.status === 401;
                    const relog =
                        isAuthProblem &&
                        (() => {
                            localStorageSave('user_info');
                            window.location.reload();
                        });
                    const actionBtnTxt = isAuthProblem ? 'Re-Login' : showRetry ? 'Retry' : UNDEF;
                    const callback = !actionBtnTxt
                        ? UNDEF
                        : isAuthProblem
                          ? relog
                          : () => {
                                Api.callV3(method, path, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
                            };
                    showSnackbar(error, 'error', UNDEF, actionBtnTxt, callback);
                }
            },
        );
    }

    static GET(
        url: string,
        respCallback?: ResponseCallback,
        showLoading: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
    ) {
        Api.call('GET', url, UNDEF, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
    }

    static POST(
        url: string,
        params: any,
        respCallback?: ResponseCallback,
        showLoading: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
    ) {
        Api.call('POST', url, params, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
    }

    static PUT(
        url: string,
        data: any,
        respCallback?: ResponseCallback,
        showLoading: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
        skipAuth = false,
    ) {
        Api.call('PUT', url, data, respCallback, showLoading, showErrorSnackbar, showRetry, headers, skipAuth);
    }

    static GETv3(
        path: string,
        respCallback?: ResponseCallback,
        showLoading: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
    ) {
        Api.callV3('GET', path, UNDEF, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
    }

    static POSTv3(
        path: string,
        params: any,
        respCallback?: ResponseCallback,
        showLoading: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
    ) {
        Api.callV3('POST', path, params, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
    }

    static PUTv3(
        path: string,
        data: any,
        respCallback?: ResponseCallback,
        showLoading: any = true,
        showErrorSnackbar = true,
        showRetry = false,
        headers?: any,
    ) {
        Api.callV3('PUT', path, data, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
    }
}
